// frontend/src/utils/authService.js

import { updateSocketToken } from "./socket";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

function normalizeStringArray(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const items = [];
  for (const entry of value) {
    const text = String(entry ?? "").trim();
    if (!text || seen.has(text)) continue;
    seen.add(text);
    items.push(text);
  }
  return items;
}

function pickObjectId(value) {
  if (value == null) return null;
  if (typeof value === "object") {
    if (typeof value._id === "string" && value._id.trim()) return value._id.trim();
    if (typeof value.id === "string" && value.id.trim()) return value.id.trim();
    return null;
  }
  const text = String(value).trim();
  return text || null;
}

function writeSessionValue(key, nextValue) {
  if (nextValue === undefined) return false;

  const currentValue = sessionStorage.getItem(key);

  if (nextValue == null) {
    if (currentValue === null) return false;
    sessionStorage.removeItem(key);
    return true;
  }

  const nextText = String(nextValue);
  if (currentValue === nextText) return false;
  sessionStorage.setItem(key, nextText);
  return true;
}

function writeSessionJson(key, nextValue) {
  if (nextValue === undefined) return false;
  return writeSessionValue(key, JSON.stringify(nextValue));
}

export function syncSessionProfile(profile) {
  if (!profile || typeof profile !== "object") return false;

  let changed = false;

  if (hasOwn(profile, "token")) {
    changed = writeSessionValue("token", profile.token) || changed;
  }

  if (hasOwn(profile, "role")) {
    changed = writeSessionValue("role", String(profile.role ?? "").trim() || null) || changed;
  }

  if (hasOwn(profile, "name")) {
    changed = writeSessionValue("name", String(profile.name ?? "").trim() || null) || changed;
  }

  if (hasOwn(profile, "email")) {
    const email = String(profile.email ?? "").trim().toLowerCase();
    changed = writeSessionValue("email", email || null) || changed;
  }

  if (hasOwn(profile, "displayRole")) {
    changed = writeSessionValue("displayRole", String(profile.displayRole ?? "").trim()) || changed;
  }

  // "local" | "saml" | "oidc" — used by sidenav.jsx to gate Download page access to
  // SSO-provisioned employee/manager accounts only. Stored whenever the field is present.
  if (hasOwn(profile, "authProvider")) {
    changed = writeSessionValue("authProvider", String(profile.authProvider ?? "").trim() || null) || changed;
  }

  const userId = hasOwn(profile, "id")
    ? pickObjectId(profile.id)
    : hasOwn(profile, "_id")
      ? pickObjectId(profile._id)
      : undefined;
  changed = writeSessionValue("userId", userId) || changed;

  if (hasOwn(profile, "tenantId")) {
    changed = writeSessionValue("tenantId", pickObjectId(profile.tenantId)) || changed;
  }

  if (hasOwn(profile, "agentId")) {
    changed = writeSessionValue("agentId", pickObjectId(profile.agentId)) || changed;
  }

  if (hasOwn(profile, "allowedViews")) {
    changed = writeSessionJson("allowedViews", normalizeStringArray(profile.allowedViews)) || changed;
  }

  if (hasOwn(profile, "allowedFeatures")) {
    changed = writeSessionJson("allowedFeatures", normalizeStringArray(profile.allowedFeatures)) || changed;
  }

  if (hasOwn(profile, "accountRoleTemplateIds")) {
    const templateIds = Array.isArray(profile.accountRoleTemplateIds)
      ? profile.accountRoleTemplateIds
          .map((item) => pickObjectId(item))
          .filter(Boolean)
      : [];
    changed = writeSessionJson("accountRoleTemplateIds", templateIds) || changed;
  }

  if (hasOwn(profile, "requiresPasswordReset")) {
    changed = writeSessionValue("requiresPasswordReset", profile.requiresPasswordReset ? "1" : null) || changed;
  }

  // Only ever false for a brand-new SSO-JIT account whose role the tenant admin hasn't actually
  // looked at yet — see User.js's ssoRoleAssigned field. Stored only when explicitly false;
  // absent/null means "assigned" (the default, true for virtually every account).
  if (hasOwn(profile, "ssoRoleAssigned")) {
    changed = writeSessionValue("ssoRoleAssigned", profile.ssoRoleAssigned === false ? "0" : null) || changed;
  }

  // Only ever true for an SSO-JIT employee/manager account with no Agent/department linked yet —
  // see User.js's ssoNeedsAllocation field. Stored only when explicitly true; absent/null/false
  // means "nothing pending" (the default, for virtually every account).
  if (hasOwn(profile, "ssoNeedsAllocation")) {
    changed = writeSessionValue("ssoNeedsAllocation", profile.ssoNeedsAllocation === true ? "1" : null) || changed;
  }

  return changed;
}

export function needsRoleAssignment() {
  return sessionStorage.getItem("ssoRoleAssigned") === "0";
}

export function needsAllocation() {
  return sessionStorage.getItem("ssoNeedsAllocation") === "1";
}

export async function loginUser(email, password) {
  try {
    const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json().catch(() => ({}));

    if (res.ok && data.token) {
      syncSessionProfile(data);
    }

    return data;

  } catch (err) {
    console.error("Login error:", err);
    return { message: "Server unreachable" };
  }
}

const _logoutChannel = typeof BroadcastChannel !== "undefined"
  ? new BroadcastChannel("gca_auth")
  : null;

// Listen for logout broadcasts from other tabs and expire this tab's session.
if (_logoutChannel) {
  _logoutChannel.onmessage = (e) => {
    if (e.data === "logout") {
      sessionStorage.clear();
      updateSocketToken(null);
      window.dispatchEvent(new Event("auth:session-expired"));
    }
  };
}

export function logoutUser() {
  // Fire-and-forget: notify the backend to revoke the current token.
  // We do not await or block on this — session is cleared regardless.
  const token = sessionStorage.getItem("token");
  if (token && BACKEND_URL) {
    fetch(`${BACKEND_URL}/api/auth/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }).catch(() => {
      // Intentionally ignored — local logout still proceeds
    });
  }

  sessionStorage.clear();
  updateSocketToken(null);

  // Notify all other tabs of the same browser to log out too
  _logoutChannel?.postMessage("logout");
}

export function getToken() {
  return sessionStorage.getItem("token");
}

export function getRole() {
  return sessionStorage.getItem("role");
}

export function getRequiresPasswordReset() {
  return sessionStorage.getItem("requiresPasswordReset") === "1";
}

export function clearRequiresPasswordReset() {
  sessionStorage.removeItem("requiresPasswordReset");
}
